import './App.css';
import { useEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import WhyChooseUs from './pages/WhyChooseUs';
import Contact from './pages/Contact';
import Calculators from './pages/Calculators';
import Dashboard from './pages/Dashboard';
import { trackPageView } from './firebase';

const SITE_NAME = 'MyFundz';
const DEFAULT_DESCRIPTION = 'MyFundz helps families and professionals with mutual funds, financial planning, and wealth creation.';

const PAGE_SEO = {
  '/': {
    title: 'Home',
    description: 'Plan your investments with MyFundz. Explore mutual funds, retirement strategies, and practical guidance for long-term wealth.',
    robots: 'index, follow',
  },
  '/about': {
    title: 'About Us',
    description: 'Learn about MyFundz and our advisor-led approach to wealth planning, portfolio discipline, and long-term financial confidence.',
    robots: 'index, follow',
  },
  '/calculators': {
    title: 'Financial Calculators',
    description: 'Use MyFundz financial calculators to estimate SIP growth, returns, and planning outcomes for your goals.',
    robots: 'index, follow',
  },
  '/services': {
    title: 'Investment Services',
    description: 'Discover MyFundz services including mutual fund advisory, retirement planning, insurance guidance, and portfolio review.',
    robots: 'index, follow',
  },
  '/why-choose-us': {
    title: 'Why Choose Us',
    description: 'See why investors choose MyFundz for transparent advice, personalized planning, and consistent portfolio support.',
    robots: 'index, follow',
  },
  '/contact': {
    title: 'Contact Us',
    description: 'Get in touch with MyFundz for investment planning, financial consultation, and personalized advisory support.',
    robots: 'index, follow',
  },
  '/dashboard': {
    title: 'Dashboard Login',
    description: 'Secure dashboard login for MyFundz administrators.',
    robots: 'noindex, nofollow',
  },
};

const setMetaByName = (name, content) => {
  if (!content) {
    return;
  }

  let tag = document.head.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
};

const setMetaByProperty = (property, content) => {
  if (!content) {
    return;
  }

  let tag = document.head.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
};

const setCanonical = (href) => {
  if (!href) {
    return;
  }

  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }

  link.setAttribute('href', href);
};

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const pageSeo = PAGE_SEO[location.pathname] || {
      title: 'Financial Planning',
      description: DEFAULT_DESCRIPTION,
      robots: 'index, follow',
    };

    const fullTitle = `${pageSeo.title} | ${SITE_NAME}`;
    const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    const canonicalUrl = `${siteUrl}${location.pathname}`;

    document.title = fullTitle;

    setMetaByName('description', pageSeo.description || DEFAULT_DESCRIPTION);
    setMetaByName('robots', pageSeo.robots || 'index, follow');
    setMetaByName('twitter:card', 'summary_large_image');
    setMetaByName('twitter:title', fullTitle);
    setMetaByName('twitter:description', pageSeo.description || DEFAULT_DESCRIPTION);

    setMetaByProperty('og:type', 'website');
    setMetaByProperty('og:site_name', SITE_NAME);
    setMetaByProperty('og:title', fullTitle);
    setMetaByProperty('og:description', pageSeo.description || DEFAULT_DESCRIPTION);
    setMetaByProperty('og:url', canonicalUrl);

    setCanonical(canonicalUrl);

    trackPageView({
      path: `${location.pathname}${location.search}`,
      title: pageSeo.title,
    });
  }, [location.pathname, location.search]);

  return null;
};

function App() {
  return (
    <BrowserRouter>
      <AnalyticsTracker />
      <div className="App">
        <Navbar />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/calculators" element={<Calculators />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/why-choose-us" element={<WhyChooseUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/dashboard" element={<Dashboard />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </BrowserRouter>
  );
}

export default App;
